import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { createColumnHelper, tableFeatures, useTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { OrcamentoResponse } from '@triade/contracts';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ProfitBadge } from '@/components/profit-badge';
import { formatarData, formatarDecimal, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AlertaCusto } from './alerta-custo';
import { SeloOrigem, SeloVencido } from './selo-vencido';
import { StatusBadge } from './status-badge';
import type { Direcao, Ordenar } from './tipos';

const features = tableFeatures({});
const col = createColumnHelper<typeof features, OrcamentoResponse>();

export interface OrcamentosTabelaProps {
  orcamentos: OrcamentoResponse[];
  ordenar: Ordenar;
  direcao: Direcao;
  /** Clicar no cabecalho: a ordenacao roda na API (muda a URL). */
  onOrdenar: (campo: Ordenar) => void;
  /** id -> nome, para "tabela · condicao" (cadastros que a tela ja carrega). */
  nomeTabela: (id: string | null) => string | undefined;
  nomeCondicao: (id: string) => string | undefined;
  onAbrir: (o: OrcamentoResponse) => void;
}

/** Grade densa da lista (TanStack Table). Ordenacao manual: so dispara `onOrdenar`; a ordem vem da API. */
export function OrcamentosTabela({ orcamentos, ordenar, direcao, onOrdenar, nomeTabela, nomeCondicao, onAbrir }: OrcamentosTabelaProps) {
  // Coluna "Origem" escondida por padrao (o controle e o menu Colunas acima da tabela).
  const [mostrarOrigem, setMostrarOrigem] = useState(false);
  const columns = useMemo(() => {
    const cab = (campo: Ordenar, rotulo: string, direita = false) => () => (
      <button
        type="button"
        onClick={() => onOrdenar(campo)}
        aria-label={`Ordenar por ${rotulo}`}
        className={cn('inline-flex items-center gap-1 font-medium hover:text-ink', direita && 'flex-row-reverse')}
      >
        {rotulo}
        {ordenar === campo ? (
          direcao === 'asc' ? <ArrowUp aria-hidden="true" className="size-3.5" /> : <ArrowDown aria-hidden="true" className="size-3.5" />
        ) : (
          <ChevronsUpDown aria-hidden="true" className="size-3.5 text-ink-disabled" />
        )}
      </button>
    );
    return [
      col.display({
        id: 'numero',
        header: cab('numero', 'Número'),
        cell: ({ row }) => (
          <div>
            <Link to="/vender/orcamentos/$id" params={{ id: row.original.id }} className="tabular font-semibold text-ink no-underline hover:underline">
              {row.original.numeroExibido}
            </Link>
            {row.original.versoesTotal > 1 && <div className="text-caption text-ink-muted">{row.original.versoesTotal} versões</div>}
            {row.original.origem === 'IMPORTADO_XARP' && <SeloOrigem origem={row.original.origem} />}
          </div>
        ),
      }),
      col.display({
        id: 'origem',
        header: 'Origem',
        cell: ({ row }) => <span className="text-ink-secondary">{row.original.origem === 'IMPORTADO_XARP' ? 'Importado do XARP' : 'App'}</span>,
      }),
      col.display({ id: 'data', header: cab('data', 'Data'), cell: ({ row }) => <span className="text-ink-secondary">{formatarData(row.original.data)}</span> }),
      col.display({ id: 'cliente', header: cab('cliente', 'Cliente'), cell: ({ row }) => row.original.cliente?.nome ?? <span className="text-ink-disabled">—</span> }),
      col.display({
        id: 'tabela',
        header: 'Tabela · condição',
        cell: ({ row }) => (
          <span className="text-ink-secondary">
            {nomeTabela(row.original.tabelaId) ?? '—'} · {nomeCondicao(row.original.condicaoId) ?? '—'}
          </span>
        ),
      }),
      col.display({
        id: 'status',
        header: cab('status', 'Situação'),
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={row.original.status} />
            {row.original.vencido && <SeloVencido />}
            <AlertaCusto orcamento={row.original} />
          </div>
        ),
      }),
      col.display({ id: 'quantidade', header: 'Qtd.', cell: ({ row }) => formatarDecimal(row.original.exibicao.quantidadeTotal, 0) }),
      col.display({
        id: 'lucro',
        header: cab('lucro', 'Lucro', true),
        cell: ({ row }) => {
          const e = row.original.exibicao;
          // Sem lucro.ver a API omite o lucro: a celula mostra so a cor da faixa (ProfitBadge sem numero).
          return e.lucroExibido == null && !e.faixa ? (
            '—'
          ) : (
            <ProfitBadge lucro={e.lucroExibido ?? undefined} faixa={e.faixa ?? undefined} meta={e.metaLucro} minimo={e.minimoLucro ?? '0'} compacto />
          );
        },
      }),
      col.display({ id: 'total', header: cab('total', 'Total', true), cell: ({ row }) => <span className="font-semibold">{formatarMoeda(row.original.exibicao.receitaTotal)}</span> }),
    ];
  }, [ordenar, direcao, onOrdenar, nomeTabela, nomeCondicao]);

  const colunasVisiveis = useMemo(() => columns.filter((c) => c.id !== 'origem' || mostrarOrigem), [columns, mostrarOrigem]);
  const table = useTable({ features, columns: colunasVisiveis, data: orcamentos, getRowId: (o) => o.id });
  const direita = new Set(['quantidade', 'lucro', 'total']);
  const mono = new Set(['numero', 'data', 'quantidade', 'total']);
  const ariaSort = (id: string): 'ascending' | 'descending' | 'none' | undefined =>
    id === ordenar ? (direcao === 'asc' ? 'ascending' : 'descending') : ['numero', 'data', 'cliente', 'status', 'lucro', 'total'].includes(id) ? 'none' : undefined;

  return (
    <>
    <div className="flex justify-end px-4 pt-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" aria-label="Colunas">
            Colunas
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuCheckboxItem checked={mostrarOrigem} onCheckedChange={(v) => setMostrarOrigem(v === true)}>
            Origem
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-small">
          <thead>
            {table.getHeaderGroups().map((g) => (
              <tr key={g.id} className="text-left text-caption text-ink-muted">
                {g.headers.map((h) => (
                  <th key={h.id} scope="col" aria-sort={ariaSort(h.column.id)} className={cn('px-3 py-2.5 font-medium first:px-4 last:px-4', direita.has(h.column.id) && 'text-right')}>
                    <table.FlexRender header={h} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                data-testid="linha-orcamento"
                onClick={() => onAbrir(row.original)}
                className="cursor-pointer border-t border-line-soft hover:bg-surface-muted"
              >
                {row.getAllCells().map((cell) => (
                  <td key={cell.id} className={cn('px-3 py-3 first:px-4 last:px-4', direita.has(cell.column.id) && 'text-right', mono.has(cell.column.id) && 'tabular')}>
                    <table.FlexRender cell={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
